import apiClient from "../../core/apiClient";

const BASE = "/contact-portal";

export type ContactPortalPeriodType = "REGULAR" | "PRE_ADVICE";

export interface ContactPortalAgentStatus {
  status: "active" | "completed" | "expired" | "revoked" | "cancelled";
  is_passkey_registered: boolean;
  registered_device_count: number;
  last_login_at?: string;
  portal_token?: string;
  portal_link?: string;
  invitation_token?: string;
  invitation_link?: string;
  invitation_expires_at?: string;
  expires_at?: string;
}

export interface ContactPortalInviteResponse {
  portal_token: string;
  portal_link: string;
  invitation_token: string;
  invitation_link: string;
  invitation_expires_at: string;
  expires_at: string;
}

export type SharingSideStatus = "not_shared" | "shared" | "revoked";
export type PairingStatus = "pending" | "active" | "revoked";

export interface AgentSpouseSharing {
  spouse_contact_id: number | null;
  spouse_name: string | null;
  family_id: number | null;
  am_i_sharing: boolean;
  is_spouse_sharing: boolean;
  my_status: SharingSideStatus;
  spouse_status: SharingSideStatus;
  pairing_status: PairingStatus;
  revoked_by_contact_id: number | null;
  revoked_by_name: string | null;
}

export interface EligibleFamilyMember {
  id: number;
  first_name: string;
  last_name: string;
  family_id: number;
}

export interface ContactPortalOverviewRow {
  contact_id: number;
  contact_name: string;
  status: "active" | "completed" | "expired" | "revoked" | "cancelled";
  is_passkey_registered: boolean;
  last_login_at: string | null;
  expires_at: string;
  family_pairing_status: PairingStatus | null;
  family_spouse_name: string | null;
}

class ContactPortalAgentService {
  async getStatus(contactId: number): Promise<ContactPortalAgentStatus> {
    const res = await apiClient.get(`${BASE}/${contactId}/status`);
    return res.data;
  }

  async createInvitation(contactId: number, periodType: ContactPortalPeriodType = "REGULAR"): Promise<ContactPortalInviteResponse> {
    const res = await apiClient.post(`${BASE}/invite/${contactId}`, { period_type: periodType });
    return res.data;
  }

  async updateExpiry(contactId: number, expiresAt: string): Promise<ContactPortalAgentStatus> {
    const res = await apiClient.put(`${BASE}/${contactId}/expiry`, { expires_at: expiresAt });
    return res.data;
  }

  async revokePortal(contactId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${contactId}/revoke`);
  }

  async cancelInvitation(contactId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${contactId}/invitation`);
  }

  async getFamilySharing(contactId: number): Promise<AgentSpouseSharing> {
    const res = await apiClient.get(`${BASE}/${contactId}/family-sharing`);
    return res.data;
  }

  async setFamilySharing(
    contactId: number,
    spouseContactId: number | null,
    familyId: number | null
  ): Promise<AgentSpouseSharing> {
    const res = await apiClient.put(`${BASE}/${contactId}/family-sharing`, {
      spouse_contact_id: spouseContactId,
      family_id: familyId,
    });
    return res.data;
  }

  async getEligibleFamilyMembers(contactId: number): Promise<EligibleFamilyMember[]> {
    const res = await apiClient.get(`${BASE}/${contactId}/family-sharing/eligible-members`);
    return res.data;
  }

  async getAllOverview(): Promise<ContactPortalOverviewRow[]> {
    const res = await apiClient.get(`${BASE}/overview`);
    return res.data;
  }
}

export const contactPortalAgentService = new ContactPortalAgentService();
